import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { fetchApiAuth } from '../utils/api';
import { Contact, ContactPayload } from './types';
import { handleError } from '../utils/helpers';

interface FetchContactsArgs {
    search?: string;
}

export const fetchContacts = createAsyncThunk<Contact[], FetchContactsArgs | void, { rejectValue: string }>(
    'contacts/fetchContacts',
    async (payload, { rejectWithValue }) => {
        try {
            const search = (payload && payload.search) ? payload.search.trim() : '';
            const url = search ? `contacts?search=${encodeURIComponent(search)}` : 'contacts';
            const response = await fetchApiAuth({
                method: 'GET',
                url,
            });
            return response.data.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

const toApiBody = (payload: ContactPayload) => ({
    name: payload.name,
    email: payload.email,
    phone_number: payload.phoneNumber,
    sort_order: payload.sortOrder,
    notes: payload.notes,
});

export const createContact = createAsyncThunk<Contact, ContactPayload, { rejectValue: string }>(
    'contacts/createContact',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'contacts',
                body: toApiBody(payload),
            });
            notification.success({
                message: 'Contact added',
                description: `${response.data.data.name} has been added.`,
            });
            return response.data.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const updateContact = createAsyncThunk<Contact, { id: number; payload: ContactPayload }, { rejectValue: string }>(
    'contacts/updateContact',
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'PUT',
                url: `contacts/${id}`,
                body: toApiBody(payload),
            });
            notification.success({
                message: 'Contact updated',
                description: `${response.data.data.name} has been updated.`,
            });
            return response.data.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const deleteContact = createAsyncThunk<number, { id: number }, { rejectValue: string }>(
    'contacts/deleteContact',
    async ({ id }, { rejectWithValue }) => {
        try {
            await fetchApiAuth({
                method: 'DELETE',
                url: `contacts/${id}`,
            });
            notification.success({
                message: 'Contact deleted',
                description: 'The contact has been deleted.',
            });
            return id;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);
